import React, { useState, useEffect, useCallback } from 'react';
import { adminService } from '../services/api';
import { ADMIN_OPS_REFRESH } from '../constants/adminEvents';
import './OrdersManagement.css';

const OrdersManagement = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [filterPayment, setFilterPayment] = useState('');
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0, pages: 0 });
  const [actionOrderId, setActionOrderId] = useState(null);
  const [trackingDraft, setTrackingDraft] = useState({});

  const fetchOrders = useCallback(async () => {
    try {
      setLoading(true);
      const params = { page: pagination.page, limit: pagination.limit };
      if (filterPayment) params.payment_status = filterPayment;
      const res = await adminService.getOrders(params);
      const list = res?.orders ?? res?.data?.orders ?? [];
      setOrders(list);
      setPagination(prev => ({
        ...prev,
        ...(res?.pagination ?? res?.data?.pagination ?? {}),
        total: res?.pagination?.total ?? res?.data?.pagination?.total ?? list.length,
        pages: res?.pagination?.pages ?? res?.data?.pagination?.pages ?? 1
      }));
    } catch (error) {
      console.error('Error fetching orders:', error);
      setMessage({ type: 'error', text: 'Error al cargar órdenes' });
      setOrders([]);
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.limit, filterPayment]);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('es-CO', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('es-CO', {
      style: 'currency',
      currency: 'COP'
    }).format(value || 0);
  };

  const getPaymentStatusBadge = (status) => {
    const badges = {
      paid: { label: 'Pagado', class: 'status-paid' },
      pending: { label: 'Pendiente', class: 'status-pending' },
      failed: { label: 'Fallido', class: 'status-failed' }
    };
    const b = badges[status] || { label: status || '-', class: 'status-default' };
    return <span className={`status-badge ${b.class}`}>{b.label}</span>;
  };

  const getOrderStatusBadge = (status) => {
    const badges = {
      pending: { label: 'Pendiente', class: 'status-pending' },
      awaiting_packaging: { label: 'En empaque', class: 'status-awaiting_packaging' },
      ready_to_ship: { label: 'Listo legacy', class: 'status-ready_to_ship' },
      processing: { label: 'Procesando', class: 'status-processing' },
      shipped: { label: 'Enviado', class: 'status-shipped' },
      delivered: { label: 'Entregado', class: 'status-delivered' },
      cancelled: { label: 'Cancelado', class: 'status-cancelled' }
    };
    const b = badges[status] || { label: status || '-', class: 'status-default' };
    return <span className={`status-badge ${b.class}`}>{b.label}</span>;
  };

  const setTracking = (orderId, value) => {
    setTrackingDraft((prev) => ({ ...prev, [orderId]: value }));
  };

  const callUpdate = async (orderId, body) => {
    try {
      setActionOrderId(orderId);
      setMessage({ type: '', text: '' });
      await adminService.updateOrderStatus(orderId, body);
      setMessage({ type: 'success', text: 'Orden actualizada' });
      await fetchOrders();
      window.dispatchEvent(new Event(ADMIN_OPS_REFRESH));
    } catch (e) {
      setMessage({
        type: 'error',
        text: e.response?.data?.message || e.message || 'Error al actualizar'
      });
    } finally {
      setActionOrderId(null);
    }
  };

  const callMarkAsPaid = async (orderId) => {
    if (!window.confirm('¿Confirmar que Wompi cobró y registrar pago? Se creará la factura pendiente y se enviará el correo de pago aprobado.')) {
      return;
    }
    try {
      setActionOrderId(orderId);
      setMessage({ type: '', text: '' });
      await adminService.markOrderAsPaid(orderId);
      setMessage({ type: 'success', text: 'Pago registrado; pedido en cola de empaque' });
      await fetchOrders();
      window.dispatchEvent(new Event(ADMIN_OPS_REFRESH));
    } catch (e) {
      setMessage({
        type: 'error',
        text: e.response?.data?.message || e.message || 'Error al marcar pago'
      });
    } finally {
      setActionOrderId(null);
    }
  };

  return (
    <div className="orders-management">
      <div className="container">
        <div className="section-header">
          <h2>📦 Órdenes</h2>
          <p>Visualiza todas las órdenes de la tienda. Las facturas se crean automáticamente cuando el pago es aprobado.</p>
        </div>

        {message.text && (
          <div className={`message-banner ${message.type}`}>
            {message.text}
            <button onClick={() => setMessage({ type: '', text: '' })}>×</button>
          </div>
        )}

        <div className="filters">
          <select
            value={filterPayment}
            onChange={(e) => {
              setFilterPayment(e.target.value);
              setPagination(prev => ({ ...prev, page: 1 }));
            }}
            className="filter-select"
          >
            <option value="">Todos los pagos</option>
            <option value="paid">Pagados</option>
            <option value="pending">Pendientes</option>
            <option value="failed">Fallidos</option>
          </select>
        </div>

        {loading ? (
          <div className="loading-container">
            <i className="fas fa-spinner fa-spin"></i>
            <span>Cargando órdenes...</span>
          </div>
        ) : orders.length === 0 ? (
          <div className="empty-state">
            <i className="fas fa-shopping-cart"></i>
            <h3>No hay órdenes</h3>
            <p>
              Las órdenes aparecen aquí cuando un cliente completa el checkout.
            </p>
          </div>
        ) : (
          <>
            <div className="orders-table-wrapper">
              <table className="orders-table">
                <thead>
                  <tr>
                    <th>Orden</th>
                    <th>Cliente</th>
                    <th>Total</th>
                    <th>Fecha</th>
                    <th>Pago</th>
                    <th>Estado</th>
                    <th>Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {orders.map((order) => (
                    <tr key={order.id}>
                      <td className="order-cell">
                        <strong>{order.order_number || `#${order.id}`}</strong>
                        {order.id != null && (
                          <span
                            className="order-id-hint"
                            title={`Probar D2: /api/payments/status/${order.id}`}
                          >
                            ID {order.id}
                          </span>
                        )}
                        {order.tracking_number ? (
                          <div className="order-tracking-hint" style={{ fontSize: '0.8rem', marginTop: '4px' }}>
                            Guía: {order.tracking_number}
                          </div>
                        ) : null}
                      </td>
                      <td>
                        <div className="customer-cell">
                          <strong>{order.customer_name || '-'}</strong>
                          <small>{order.customer_email}</small>
                        </div>
                      </td>
                      <td>{formatCurrency(order.total)}</td>
                      <td>{formatDate(order.created_at)}</td>
                      <td>{getPaymentStatusBadge(order.payment_status)}</td>
                      <td>{getOrderStatusBadge(order.status)}</td>
                      <td className="order-actions-cell">
                        {order.payment_status === 'pending' && order.status === 'pending' && (
                          <button
                            type="button"
                            className="btn-ord"
                            style={{ background: 'rgba(153,0,0,0.1)', fontWeight: 600 }}
                            disabled={actionOrderId === order.id}
                            onClick={() => callMarkAsPaid(order.id)}
                          >
                            Marcar como pagado
                          </button>
                        )}
                        {order.payment_status === 'paid' &&
                          ['pending', 'awaiting_packaging', 'ready_to_ship'].includes(order.status) && (
                          <button
                            type="button"
                            className="btn-ord"
                            disabled={actionOrderId === order.id}
                            onClick={() => callUpdate(order.id, { status: 'processing' })}
                          >
                            A procesar
                          </button>
                        )}
                        {order.status === 'processing' && (
                          <div className="shipped-row">
                            <input
                              type="text"
                              placeholder="N° guía / tracking"
                              value={trackingDraft[order.id] ?? ''}
                              onChange={(e) => setTracking(order.id, e.target.value)}
                              style={{ maxWidth: '140px', marginRight: '6px' }}
                            />
                            <button
                              type="button"
                              className="btn-ord"
                              disabled={actionOrderId === order.id}
                              onClick={() =>
                                callUpdate(order.id, {
                                  status: 'shipped',
                                  tracking_number: (trackingDraft[order.id] || '').trim()
                                })
                              }
                            >
                              Enviar
                            </button>
                          </div>
                        )}
                        {order.status === 'shipped' && (
                          <button
                            type="button"
                            className="btn-ord"
                            disabled={actionOrderId === order.id}
                            onClick={() => callUpdate(order.id, { status: 'delivered' })}
                          >
                            Entregado
                          </button>
                        )}
                        {['pending', 'processing', 'awaiting_packaging', 'ready_to_ship'].includes(
                          order.status
                        ) && (
                          <button
                            type="button"
                            className="btn-ord btn-ord-danger"
                            disabled={actionOrderId === order.id}
                            onClick={() => {
                              if (window.confirm('¿Cancelar esta orden?')) {
                                callUpdate(order.id, { status: 'cancelled' });
                              }
                            }}
                          >
                            Cancelar
                          </button>
                        )}
                        {!['pending', 'processing', 'shipped', 'awaiting_packaging', 'ready_to_ship'].includes(
                          order.status
                        ) && (
                          <span className="order-actions-none">—</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {pagination.pages > 1 && (
              <div className="pagination">
                <button
                  disabled={pagination.page <= 1}
                  onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                >
                  ← Anterior
                </button>
                <span>Página {pagination.page} de {pagination.pages}</span>
                <button
                  disabled={pagination.page >= pagination.pages}
                  onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                >
                  Siguiente →
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default OrdersManagement;
